import { useState, useCallback, useRef, useEffect } from 'react'
import './WorldMap.css'

// API response types matching the Go backend
interface RegionBounds {
  minRow: number
  maxRow: number
  minCol: number
  maxCol: number
}

interface RegionCenter {
  lat: number
  lon: number
}

interface Region {
  id: string
  name: string
  bounds: RegionBounds
  center: RegionCenter
}

interface AsciiWorldResponse {
  map: string
  width: number
  height: number
  zoom: number
  regions: Region[]
}

// Projection types supported by the backend
type Projection = 'equirect' | 'kavrayskiy' | 'lambert' | 'hammer'

// Zoom presets for the UI
const ZOOM_PRESETS = [
  { name: 'WORLD', zoom: 1.0 },
  { name: 'CONTINENTAL', zoom: 2.0 },
  { name: 'REGIONAL', zoom: 4.0 },
]

// API base URL - use Cloud Run in production
const API_BASE = import.meta.env.VITE_API_URL || 'https://tradewars-server-156160925122.us-central1.run.app'

export default function WorldMap() {
  // Map state
  const [mapData, setMapData] = useState<AsciiWorldResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // View parameters
  const [zoomIndex, setZoomIndex] = useState(0)
  const [centerLon, setCenterLon] = useState(0)
  const [centerLat, setCenterLat] = useState(0)
  const [projection, setProjection] = useState<Projection>('equirect')
  const [dayNight, setDayNight] = useState(false)

  // UI state
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null)
  const [hoveredRegion, setHoveredRegion] = useState<Region | null>(null)
  const [cursorPos, setCursorPos] = useState<{ row: number; col: number } | null>(null)

  const mapRef = useRef<HTMLPreElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)

  // Drag panning state
  const isDragging = useRef(false)
  const wasDragging = useRef(false)
  const dragStart = useRef<{ x: number; y: number; lon: number; lat: number } | null>(null)

  // AbortController for cancelling in-flight requests during rapid updates
  const abortControllerRef = useRef<AbortController | null>(null)

  // Calculate dimensions based on zoom level
  const getDimensions = (zoomIdx: number) => {
    const preset = ZOOM_PRESETS[zoomIdx]!
    // Higher zoom = larger render for more detail
    if (preset.zoom <= 1) return { width: 80, height: 24 }
    if (preset.zoom <= 2) return { width: 120, height: 36 }
    return { width: 160, height: 48 }
  }

  // Fetch map data from backend
  const fetchMap = useCallback(async () => {
    // Cancel any in-flight request to prevent race conditions during rapid drag
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)

    const preset = ZOOM_PRESETS[zoomIndex]!
    const { width, height } = getDimensions(zoomIndex)

    const params = new URLSearchParams({
      width: String(width),
      height: String(height),
      zoom: String(preset.zoom),
      lon: String(centerLon),
      lat: String(centerLat),
      projection,
      daynight: String(dayNight),
    })

    try {
      const response = await fetch(`${API_BASE}/api/v1/asciiworld?${params}`, {
        signal: abortControllerRef.current.signal,
      })
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }
      const data: AsciiWorldResponse = await response.json()
      setMapData(data)
      setSelectedRegion(null)
      setHoveredRegion(null)
      setLoading(false)
    } catch (err) {
      // Ignore abort errors - they're expected during rapid drag
      if (err instanceof Error && err.name === 'AbortError') {
        // Don't update loading state for aborted requests
        return
      }
      console.error('Failed to fetch map:', err)
      setError(err instanceof Error ? err.message : 'Failed to load map')
      setLoading(false)
    }
  }, [zoomIndex, centerLon, centerLat, projection, dayNight])

  // Initial load and refetch on parameter changes
  useEffect(() => {
    fetchMap()
  }, [fetchMap])

  // Cleanup: cancel any in-flight request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Cycle through zoom levels
  const handleZoom = () => {
    setZoomIndex((prev) => (prev + 1) % ZOOM_PRESETS.length)
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0
      viewportRef.current.scrollTop = 0
    }
  }

  // Toggle day/night shading
  const handleDayNight = () => {
    setDayNight((prev) => !prev)
  }

  // Cycle projections
  const handleProjection = () => {
    const projections: Projection[] = ['equirect', 'kavrayskiy', 'lambert', 'hammer']
    const currentIdx = projections.indexOf(projection)
    setProjection(projections[(currentIdx + 1) % projections.length]!)
  }

  // Center the viewport
  const handleCenter = () => {
    setCenterLon(0)
    setCenterLat(0)
    if (viewportRef.current && mapRef.current) {
      const viewport = viewportRef.current
      const map = mapRef.current
      viewport.scrollLeft = (map.scrollWidth - viewport.clientWidth) / 2
      viewport.scrollTop = (map.scrollHeight - viewport.clientHeight) / 2
    }
  }

  // Calculate character position from mouse click
  const getCharPosition = useCallback(
    (e: React.MouseEvent<HTMLPreElement>) => {
      const pre = mapRef.current
      if (!pre) return null

      const rect = pre.getBoundingClientRect()
      const style = window.getComputedStyle(pre)
      const fontSize = parseFloat(style.fontSize)
      const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.2

      const paddingLeft = parseFloat(style.paddingLeft) || 0
      const paddingTop = parseFloat(style.paddingTop) || 0

      // VT323 character width approximation
      const charWidth = fontSize * 0.55

      const x = e.clientX - rect.left - paddingLeft
      const y = e.clientY - rect.top - paddingTop

      if (x < 0 || y < 0) return null

      const col = Math.floor(x / charWidth)
      const row = Math.floor(y / lineHeight)

      return { row, col }
    },
    []
  )

  // Find region at position
  const findRegionAtPosition = useCallback(
    (row: number, col: number): Region | null => {
      if (!mapData?.regions) return null

      for (const region of mapData.regions) {
        if (
          row >= region.bounds.minRow &&
          row <= region.bounds.maxRow &&
          col >= region.bounds.minCol &&
          col <= region.bounds.maxCol
        ) {
          return region
        }
      }
      return null
    },
    [mapData]
  )

  // Handle click on map
  const handleMapClick = useCallback(
    (e: React.MouseEvent<HTMLPreElement>) => {
      // Ignore clicks that were part of a drag operation
      if (wasDragging.current) {
        wasDragging.current = false
        return
      }

      const pos = getCharPosition(e)
      if (!pos) return

      const region = findRegionAtPosition(pos.row, pos.col)
      if (region) {
        setSelectedRegion(region)
        // Center on clicked region
        setCenterLon(region.center.lon)
        setCenterLat(region.center.lat)
      }
    },
    [getCharPosition, findRegionAtPosition]
  )

  // Handle mouse move for hover detection
  const handleMapMouseMove = useCallback(
    (e: React.MouseEvent<HTMLPreElement>) => {
      const pos = getCharPosition(e)
      if (!pos) {
        setCursorPos(null)
        return
      }

      setCursorPos(pos)
      const region = findRegionAtPosition(pos.row, pos.col)
      setHoveredRegion(region)
    },
    [getCharPosition, findRegionAtPosition]
  )

  // Handle mouse leave
  const handleMapMouseLeave = useCallback(() => {
    setHoveredRegion(null)
    setCursorPos(null)
  }, [])

  // Calculate degrees per pixel for drag panning
  const getDegreesPerPixel = useCallback(() => {
    const preset = ZOOM_PRESETS[zoomIndex]!
    const { width, height } = getDimensions(zoomIndex)
    const pre = mapRef.current
    if (!pre) return { lon: 1, lat: 1 }

    const style = window.getComputedStyle(pre)
    const fontSize = parseFloat(style.fontSize)
    const charWidth = fontSize * 0.55
    const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.2

    // Calculate actual pixel dimensions of the map
    const mapWidthPx = width * charWidth
    const mapHeightPx = height * lineHeight

    // Geographic range covered by the map at current zoom
    const lonRange = 360 / preset.zoom
    const latRange = 180 / preset.zoom

    return {
      lon: lonRange / mapWidthPx,
      lat: latRange / mapHeightPx,
    }
  }, [zoomIndex])

  // Handle mouse down - start drag
  const handleMapMouseDown = useCallback(
    (e: React.MouseEvent<HTMLPreElement>) => {
      // Only start drag on left mouse button
      if (e.button !== 0) return

      isDragging.current = true
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        lon: centerLon,
        lat: centerLat,
      }

      // Add grabbing cursor
      if (mapRef.current) {
        mapRef.current.style.cursor = 'grabbing'
      }

      // Prevent text selection during drag
      e.preventDefault()
    },
    [centerLon, centerLat]
  )

  // Handle mouse up - end drag
  const handleMapMouseUp = useCallback(() => {
    if (isDragging.current) {
      wasDragging.current = true
      // Reset after a short delay to allow click handler to check
      setTimeout(() => {
        wasDragging.current = false
      }, 50)
    }
    isDragging.current = false
    dragStart.current = null

    // Restore grab cursor
    if (mapRef.current) {
      mapRef.current.style.cursor = 'grab'
    }
  }, [])

  // Handle drag movement
  const handleDragMove = useCallback(
    (e: React.MouseEvent<HTMLPreElement>) => {
      if (!isDragging.current || !dragStart.current) return

      const dx = e.clientX - dragStart.current.x
      const dy = e.clientY - dragStart.current.y

      // Only trigger update if we've moved enough (prevents micro-movements)
      if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return

      const degreesPerPixel = getDegreesPerPixel()

      // Calculate new center (drag direction is inverted - dragging right moves map left, showing more to the right)
      const newLon = dragStart.current.lon - dx * degreesPerPixel.lon
      const newLat = dragStart.current.lat + dy * degreesPerPixel.lat // y is inverted in screen coords

      // Clamp latitude to valid range
      const clampedLat = Math.max(-90, Math.min(90, newLat))

      // Update center position
      setCenterLon(newLon)
      setCenterLat(clampedLat)

      // Update drag start to current position for continuous dragging feel
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        lon: newLon,
        lat: clampedLat,
      }
    },
    [getDegreesPerPixel]
  )

  // Combined mouse move handler for both hover detection and drag
  const handleCombinedMouseMove = useCallback(
    (e: React.MouseEvent<HTMLPreElement>) => {
      // Handle drag if active
      if (isDragging.current) {
        handleDragMove(e)
        return
      }

      // Otherwise handle hover detection
      const pos = getCharPosition(e)
      if (!pos) {
        setCursorPos(null)
        return
      }

      setCursorPos(pos)
      const region = findRegionAtPosition(pos.row, pos.col)
      setHoveredRegion(region)
    },
    [handleDragMove, getCharPosition, findRegionAtPosition]
  )

  // Global mouse up listener to catch releases outside the element
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging.current) {
        handleMapMouseUp()
      }
    }

    document.addEventListener('mouseup', handleGlobalMouseUp)
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp)
  }, [handleMapMouseUp])

  // Keyboard navigation for panning
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const step = 40
      const panStep = 10 // degrees
      switch (e.key) {
        case 'ArrowUp':
          viewport.scrollTop -= step
          e.preventDefault()
          break
        case 'ArrowDown':
          viewport.scrollTop += step
          e.preventDefault()
          break
        case 'ArrowLeft':
          viewport.scrollLeft -= step
          e.preventDefault()
          break
        case 'ArrowRight':
          viewport.scrollLeft += step
          e.preventDefault()
          break
        case '+':
        case '=':
          handleZoom()
          e.preventDefault()
          break
        case 'w':
        case 'W':
          setCenterLat((prev) => Math.min(90, prev + panStep))
          e.preventDefault()
          break
        case 's':
        case 'S':
          setCenterLat((prev) => Math.max(-90, prev - panStep))
          e.preventDefault()
          break
        case 'a':
        case 'A':
          setCenterLon((prev) => prev - panStep)
          e.preventDefault()
          break
        case 'd':
        case 'D':
          setCenterLon((prev) => prev + panStep)
          e.preventDefault()
          break
        case 'n':
        case 'N':
          handleDayNight()
          e.preventDefault()
          break
        case 'p':
        case 'P':
          handleProjection()
          e.preventDefault()
          break
      }
    }

    viewport.addEventListener('keydown', handleKeyDown)
    return () => viewport.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Get display info for info panel
  const displayRegion = selectedRegion || hoveredRegion

  // Format projection name for display
  const projectionName = {
    equirect: 'EQUIRECT',
    kavrayskiy: 'KAVRAY',
    lambert: 'LAMBERT',
    hammer: 'HAMMER',
  }[projection]

  return (
    <div className="worldmap-app">
      {/* Toolbar */}
      <div className="worldmap-toolbar">
        <button className="worldmap-btn" onClick={handleZoom} title="Cycle zoom levels (+)">
          [ZOOM]
        </button>
        <span className="worldmap-level">
          {zoomIndex + 1}/{ZOOM_PRESETS.length}: {ZOOM_PRESETS[zoomIndex]?.name}
        </span>
        <span className="worldmap-separator">|</span>
        <button className="worldmap-btn" onClick={handleProjection} title="Cycle projections (P)">
          [{projectionName}]
        </button>
        <span className="worldmap-separator">|</span>
        <button
          className={`worldmap-btn ${dayNight ? 'active' : ''}`}
          onClick={handleDayNight}
          title="Toggle day/night (N)"
        >
          [{dayNight ? 'DAY/NIGHT' : 'STATIC'}]
        </button>
        <span className="worldmap-separator">|</span>
        <button className="worldmap-btn" onClick={handleCenter} title="Center map">
          [CENTER]
        </button>
        <span className="worldmap-separator">|</span>
        <button className="worldmap-btn" onClick={fetchMap} title="Refresh map">
          [REFRESH]
        </button>
      </div>

      {/* Map Viewport */}
      <div
        ref={viewportRef}
        className="worldmap-viewport"
        tabIndex={0}
        role="application"
        aria-label="World Map - use arrow keys to scroll, WASD to pan, click to select regions"
      >
        {loading && (
          <div className="worldmap-loading">
            <span>Loading map data...</span>
          </div>
        )}
        {error && (
          <div className="worldmap-error">
            <span>Error: {error}</span>
            <button className="worldmap-btn" onClick={fetchMap}>
              [RETRY]
            </button>
          </div>
        )}
        {!loading && !error && mapData && (
          <pre
            ref={mapRef}
            className={`worldmap-canvas ${hoveredRegion ? 'has-hover' : ''}`}
            onClick={handleMapClick}
            onMouseDown={handleMapMouseDown}
            onMouseMove={handleCombinedMouseMove}
            onMouseLeave={handleMapMouseLeave}
          >
            {mapData.map}
          </pre>
        )}
      </div>

      {/* Info Panel */}
      <div className="worldmap-info">
        {cursorPos && (
          <span className="worldmap-info-coords">
            [{cursorPos.row},{cursorPos.col}]
          </span>
        )}
        {mapData && (
          <span className="worldmap-info-stats">
            {mapData.width}x{mapData.height} | {mapData.regions?.length || 0} regions
          </span>
        )}
        <span className="worldmap-separator">|</span>
        {displayRegion ? (
          <>
            <span className="worldmap-info-label">REGION:</span>
            <span className="worldmap-info-name">{displayRegion.name}</span>
            <span className="worldmap-info-separator">-</span>
            <span className="worldmap-info-fact">
              [{displayRegion.id}] {displayRegion.center.lat.toFixed(1)}°,{' '}
              {displayRegion.center.lon.toFixed(1)}°
            </span>
          </>
        ) : (
          <span className="worldmap-info-hint">
            Click region to zoom. WASD=pan, +/P/N=controls.
          </span>
        )}
      </div>
    </div>
  )
}
